import { useEffect, useState, useCallback } from 'react';
import { postsAPI } from '../services/api';
import { Table, Badge, Btn, Modal, Card, Pagination, useToast, Input, Select, Spinner } from '../components/UI';

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Posts() {
  const { show, ToastEl } = useToast();
  const [posts, setPosts]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [meta, setMeta]             = useState({});
  const [statusFilter, setStatus]   = useState('');
  const [search, setSearch]         = useState('');
  const [categoryFilter, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editPost, setEditPost]     = useState(null);
  const [viewPost, setViewPost]     = useState(null);
  const [showManageCats, setShowManageCats] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await postsAPI.listCategories();
      setCategories(data.data);
    } catch { /* silent */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await postsAPI.list({
        page, limit: 15, status: statusFilter, search,
        category_id: categoryFilter || undefined,
      });
      setPosts(data.data);
      setMeta(data.pagination);
    } catch { show('Failed to load posts', 'error'); }
    finally { setLoading(false); }
  }, [page, statusFilter, search, categoryFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  async function deletePost(id, title) {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await postsAPI.delete(id);
      show('Post deleted');
      load();
    } catch (e) { show(e.response?.data?.message || 'Failed to delete post', 'error'); }
  }

  const columns = [
    { key: 'title', label: 'Title', render: (v, row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {row.photo_path && (
          <img src={postsAPI.photoUrl(row.id)} alt="" style={{ width: 38, height: 38, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}/>
        )}
        <div>
          <div style={{ fontWeight: 500 }}>{v}</div>
          {row.category_name && (
            <div style={{ fontSize: 11, color: 'var(--accent2)', fontWeight: 500 }}>{row.category_name}</div>
          )}
          {row.description && (
            <div style={{ fontSize: 11, color: 'var(--text3)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.description}
            </div>
          )}
        </div>
      </div>
    )},
    { key: 'status',     label: 'Status',  render: v => <Badge label={v} type={v}/> },
    { key: 'created_by', label: 'By',      render: v => <span style={{ fontSize: 12 }}>{v}</span> },
    { key: 'created_at', label: 'Date',    render: v => new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) },
    { key: 'actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 6 }}>
        <Btn size="sm" variant="outline" onClick={() => setViewPost(row)}>View</Btn>
        <Btn size="sm" variant="ghost"   onClick={() => setEditPost(row)}>Edit</Btn>
        <Btn size="sm" variant="danger"  onClick={() => deletePost(row.id, row.title)}>Delete</Btn>
      </div>
    )},
  ];

  return (
    <div style={{ padding: '28px 32px', animation: 'fadeIn .35s ease' }}>
      {ToastEl}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 26, fontWeight: 700 }}>Posts</h1>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>Create and manage posts with rich multilingual content.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="ghost" onClick={() => setShowManageCats(true)}>⚙ Categories</Btn>
          <Btn variant="primary" onClick={() => setShowCreate(true)}>+ New Post</Btn>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search posts…"
          style={{
            padding: '7px 12px', borderRadius: 'var(--r)', border: '1px solid var(--border)',
            background: 'var(--surface)', color: 'var(--text)', fontSize: 13, outline: 'none', minWidth: 200,
          }}
        />
        {/* Category filter pills */}
        <button onClick={() => { setCategory(''); setPage(1); }} style={{
          padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500,
          border: '1px solid var(--border)', cursor: 'pointer',
          background: categoryFilter === '' ? 'var(--accent)' : 'transparent',
          color: categoryFilter === '' ? '#fff' : 'var(--text2)',
        }}>All</button>
        {categories.map(c => (
          <button key={c.id} onClick={() => { setCategory(String(c.id)); setPage(1); }} style={{
            padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500,
            border: '1px solid var(--border)', cursor: 'pointer',
            background: categoryFilter === String(c.id) ? 'var(--accent)' : 'transparent',
            color: categoryFilter === String(c.id) ? '#fff' : 'var(--text2)',
          }}>
            {c.name}
            <span style={{ marginLeft: 5, opacity: .7, fontSize: 11 }}>({c.post_count})</span>
          </button>
        ))}
        {/* Status filter */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {['', 'published', 'draft', 'archived'].map(s => (
            <button key={s} onClick={() => { setStatus(s); setPage(1); }} style={{
              padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500,
              border: '1px solid var(--border)', cursor: 'pointer',
              background: statusFilter === s ? 'var(--surface2)' : 'transparent',
              color: 'var(--text2)', fontStyle: 'normal',
              outline: statusFilter === s ? '1.5px solid var(--accent)' : 'none',
            }}>
              {s === '' ? 'All status' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <Card>
        <Table columns={columns} data={posts} loading={loading}/>
        <Pagination page={page} pages={meta.pages} total={meta.total} limit={15} onChange={setPage}/>
      </Card>

      {viewPost && (
        <ViewModal
          postId={viewPost.id}
          onClose={() => setViewPost(null)}
          onEdit={() => { setViewPost(null); setEditPost(viewPost); }}
        />
      )}

      {showCreate && (
        <PostFormModal
          categories={categories}
          onClose={() => setShowCreate(false)}
          onSuccess={() => { setShowCreate(false); load(); loadCategories(); show('Post created'); }}
          onError={msg => show(msg, 'error')}
        />
      )}

      {editPost && (
        <PostFormModal
          post={editPost}
          categories={categories}
          onClose={() => setEditPost(null)}
          onSuccess={() => { setEditPost(null); load(); loadCategories(); show('Post updated'); }}
          onError={msg => show(msg, 'error')}
        />
      )}

      {showManageCats && (
        <ManageCategoriesModal
          onClose={() => { setShowManageCats(false); loadCategories(); }}
          onToast={show}
        />
      )}
    </div>
  );
}

// ── View Modal ────────────────────────────────────────────────────────────────
function ViewModal({ postId, onClose, onEdit }) {
  const [post, setPost]       = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    postsAPI.get(postId)
      .then(r => setPost(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [postId]);

  return (
    <Modal title="" onClose={onClose} width={680}>
      {loading || !post ? (
        <div style={{ padding: '60px 0', textAlign: 'center' }}><Spinner size={28}/></div>
      ) : (
        <>
          <div style={{ margin: '-20px -20px 20px', padding: '16px 20px', borderBottom: '1px solid var(--border)', background: 'var(--surface2)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{post.title}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {post.category_name && (
                  <span style={{ background: 'var(--accent-light)', color: 'var(--accent2)', borderRadius: 99, padding: '1px 8px', fontWeight: 600, fontSize: 10 }}>
                    {post.category_name}
                  </span>
                )}
                {new Date(post.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                {' · '}{post.created_by}{' · '}
                <Badge label={post.status} type={post.status}/>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <Btn size="sm" variant="ghost" onClick={onEdit}>Edit</Btn>
              <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text3)', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
            </div>
          </div>

          {post.photo_path && (
            <div style={{ marginBottom: 20, borderRadius: 'var(--r)', overflow: 'hidden', maxHeight: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface2)' }}>
              <img src={postsAPI.photoUrl(post.id)} alt={post.title} style={{ width: '100%', maxHeight: 320, objectFit: 'cover', display: 'block' }}/>
            </div>
          )}

          {post.description && (
            <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>{post.description}</p>
          )}

          {post.content && (
            <div style={{ background: 'var(--surface2)', borderRadius: 'var(--r)', padding: '18px 20px', maxHeight: 400, overflowY: 'auto' }}>
              <p dir="auto" style={{ margin: 0, fontSize: 15, lineHeight: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--text)' }}>
                {post.content}
              </p>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

// ── Create / Edit Modal ───────────────────────────────────────────────────────
function PostFormModal({ post, categories, onClose, onSuccess, onError }) {
  const isEdit = Boolean(post);
  const [form, setForm] = useState({
    title:       post?.title       || '',
    description: post?.description || '',
    content:     post?.content     || '',
    status:      post?.status      || 'published',
    category_id: post?.category_id ? String(post.category_id) : '',
  });
  const [photo, setPhoto]     = useState(null);
  const [saving, setSaving]   = useState(false);
  const [loadingPost, setLoadingPost] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    postsAPI.get(post.id)
      .then(r => {
        const p = r.data.data;
        setForm({
          title:       p.title       || '',
          description: p.description || '',
          content:     p.content     || '',
          status:      p.status      || 'published',
          category_id: p.category_id ? String(p.category_id) : '',
        });
      })
      .catch(() => onError('Failed to load post data'))
      .finally(() => setLoadingPost(false));
  }, []);

  async function submit() {
    if (!form.title.trim()) return onError('Title is required');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title',       form.title.trim());
      fd.append('description', form.description);
      fd.append('content',     form.content);
      fd.append('status',      form.status);
      if (form.category_id) fd.append('category_id', form.category_id);
      if (photo) fd.append('photo', photo);

      if (isEdit) {
        await postsAPI.update(post.id, fd);
      } else {
        await postsAPI.create(fd);
      }
      onSuccess();
    } catch (e) { onError(e.response?.data?.message || 'Failed to save post'); }
    finally { setSaving(false); }
  }

  return (
    <Modal title={isEdit ? 'Edit post' : 'New post'} onClose={onClose} width={580}>
      {loadingPost ? (
        <div style={{ padding: '60px 0', textAlign: 'center' }}><Spinner size={28}/></div>
      ) : null}
      <div style={{ display: loadingPost ? 'none' : 'flex', flexDirection: 'column', gap: 14 }}>

        <Input
          label="Title *"
          value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          placeholder="Post title"
        />

        <Select
          label="Category"
          value={form.category_id}
          onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
        >
          <option value="">— No category —</option>
          {categories.map(c => (
            <option key={c.id} value={String(c.id)}>{c.name}</option>
          ))}
        </Select>

        <Input
          label="Description"
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="Short description shown in list view"
        />

        {/* Photo upload */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)', marginBottom: 6 }}>
            Photo {isEdit && post?.photo_path ? '(leave empty to keep existing)' : '(optional)'}
          </div>
          {isEdit && post?.photo_path && !photo && (
            <img src={postsAPI.photoUrl(post.id)} alt="" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6, marginBottom: 8, display: 'block' }}/>
          )}
          <input type="file" accept="image/*" onChange={e => setPhoto(e.target.files[0])} style={{ fontSize: 13 }}/>
          {photo && (
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
              {photo.name} · {(photo.size / 1024).toFixed(0)} KB
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)' }}>
            Content <span style={{ fontWeight: 400, color: 'var(--text3)' }}>(Malayalam, English, Urdu, Tamil supported)</span>
          </label>
          <textarea
            dir="auto"
            value={form.content}
            onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
            rows={10}
            placeholder="Write post content here…"
            style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--r)', padding: '10px 12px', fontSize: 14,
              color: 'var(--text)', outline: 'none', width: '100%',
              resize: 'vertical', fontFamily: 'var(--sans)', lineHeight: 1.9,
            }}
          />
        </div>

        <Select
          label="Status"
          value={form.status}
          onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
        >
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
        </Select>

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <Btn onClick={submit} disabled={saving}>
            {saving ? <Spinner size={14} color="#fff"/> : (isEdit ? 'Save changes' : 'Create post')}
          </Btn>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ── Manage Categories Modal ───────────────────────────────────────────────────
function ManageCategoriesModal({ onClose, onToast }) {
  const [cats, setCats]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView]       = useState('list'); // 'list' | 'create' | 'edit'
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState({ name: '', slug: '', sort_order: '0' });
  const [saving, setSaving]   = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await postsAPI.listCategories();
      setCats(data.data);
    } catch { onToast('Failed to load categories', 'error'); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  function openCreate() {
    setForm({ name: '', slug: '', sort_order: '0' });
    setEditing(null);
    setView('create');
  }

  function openEdit(cat) {
    setForm({ name: cat.name, slug: cat.slug, sort_order: String(cat.sort_order) });
    setEditing(cat);
    setView('edit');
  }

  function autoSlug(name) {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  }

  async function save() {
    if (!form.name.trim() || !form.slug.trim()) return;
    setSaving(true);
    try {
      const body = { name: form.name.trim(), slug: form.slug.trim(), sort_order: parseInt(form.sort_order) || 0 };
      if (view === 'create') {
        await postsAPI.createCategory(body);
        onToast('Category created');
      } else {
        await postsAPI.updateCategory(editing.id, body);
        onToast('Category updated');
      }
      await load();
      setView('list');
    } catch (e) { onToast(e.response?.data?.message || 'Failed to save', 'error'); }
    finally { setSaving(false); }
  }

  async function confirmDelete(cat) {
    try {
      await postsAPI.deleteCategory(cat.id);
      onToast('Category deleted');
      setDeleteConfirm(null);
      load();
    } catch (e) { onToast(e.response?.data?.message || 'Failed to delete', 'error'); }
  }

  return (
    <Modal title="Manage Post Categories" onClose={onClose} width={480}>
      {view === 'list' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
            <Btn onClick={openCreate}>+ Add Category</Btn>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 32 }}><Spinner/></div>
          ) : cats.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--text3)', fontSize: 13 }}>No categories yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {cats.map(cat => (
                <div key={cat.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                  background: 'var(--surface2)', borderRadius: 'var(--r)', border: '1px solid var(--border)',
                  position: 'relative',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{cat.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                      /{cat.slug} · {cat.post_count} post{cat.post_count !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <Btn size="sm" variant="ghost" onClick={() => openEdit(cat)}>Edit</Btn>
                  <Btn size="sm" variant="danger" onClick={() => setDeleteConfirm(cat)}>Delete</Btn>

                  {/* Delete confirm overlay */}
                  {deleteConfirm?.id === cat.id && (
                    <div style={{
                      position: 'absolute', inset: 0, borderRadius: 'var(--r)',
                      background: 'var(--red-bg, #fff5f5)', border: '1px solid #f5c6c2',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '0 14px',
                    }}>
                      <span style={{ fontSize: 12, color: 'var(--red, #c0392b)', flex: 1 }}>Delete "{cat.name}"?</span>
                      <Btn size="sm" variant="danger" onClick={() => confirmDelete(cat)}>Yes, delete</Btn>
                      <Btn size="sm" variant="ghost"  onClick={() => setDeleteConfirm(null)}>Cancel</Btn>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {(view === 'create' || view === 'edit') && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            label="Name *"
            value={form.name}
            onChange={e => {
              const name = e.target.value;
              setForm(f => ({ ...f, name, slug: view === 'create' ? autoSlug(name) : f.slug }));
            }}
            placeholder="e.g. Islamic Studies"
          />
          <Input
            label="Slug *"
            value={form.slug}
            onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
            placeholder="e.g. islamic-studies"
          />
          <Input
            label="Sort order"
            type="number"
            value={form.sort_order}
            onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
            placeholder="0"
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <Btn onClick={save} disabled={saving || !form.name.trim() || !form.slug.trim()}>
              {saving ? <Spinner size={14} color="#fff"/> : (view === 'create' ? 'Create' : 'Save changes')}
            </Btn>
            <Btn variant="ghost" onClick={() => setView('list')}>Back</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}
